import { useRouter } from "@tanstack/react-router";
import React, { createContext, useContext, useEffect, useState } from "react";
import { VoiceService, type VoiceSettings, type VoiceState } from "../services/VoiceService";

interface VoiceContextValue {
    voiceService: VoiceService;
    state: VoiceState;
    speak: (text: string) => void;
    toggle: (text?: string) => void;
    switchTo: (text: string) => void;
    stop: () => void;
    pause: () => void;
    resume: () => void;
    updateSettings: (settings: Partial<VoiceSettings>) => void;
}

const VoiceContext = createContext<VoiceContextValue | null>(null);

interface VoiceProviderProps {
    children: React.ReactNode;
}

export function VoiceProvider({ children }: VoiceProviderProps) {
    const [voiceService] = useState(() => new VoiceService());
    const [state, setState] = useState<VoiceState>(() => voiceService.getState());
    const router = useRouter();

    useEffect(() => {
        const handleStateChange = (newState: VoiceState) => {
            setState(newState);
        };

        voiceService.onStateChange = handleStateChange;

        const handleVoicesChanged = () => {
            setState(voiceService.getState());
        };

        if (voiceService.isSupported) {
            window.speechSynthesis.addEventListener("voiceschanged", handleVoicesChanged);
        }

        // Subscribe to router navigation events to stop voice immediately
        const unsubscribe = router.subscribe("onBeforeNavigate", () => {
            // Stop voice immediately when navigation begins
            voiceService.stop();
        });

        return () => {
            if (voiceService.isSupported) {
                window.speechSynthesis.removeEventListener("voiceschanged", handleVoicesChanged);
            }
            voiceService.stop();
            unsubscribe();
        };
    }, [voiceService, router]);

    const contextValue: VoiceContextValue = {
        voiceService,
        state,
        speak: (text: string) => voiceService.speak(text),
        toggle: (text?: string) => voiceService.toggle(text),
        switchTo: (text: string) => voiceService.switchTo(text),
        stop: () => voiceService.stop(),
        pause: () => voiceService.pause(),
        resume: () => voiceService.resume(),
        updateSettings: (settings: Partial<VoiceSettings>) => voiceService.updateSettings(settings),
    };

    return <VoiceContext.Provider value={contextValue}>{children}</VoiceContext.Provider>;
}

export function useVoice(): VoiceContextValue {
    const context = useContext(VoiceContext);
    if (!context) {
        throw new Error("useVoice must be used within a VoiceProvider");
    }
    return context;
}
